import * as React from 'react';
import { IndexRoute, Redirect, Route } from 'react-router';

import { GradedDiscussionPromptItemDynamic } from '@coursera-web/graded-discussion-prompt';
import { InjectableItemPageDynamic } from '@coursera-web/item-injectable';
import { WidgetItemDynamic } from '@coursera-web/item-widget';
import { ITEM_ROUTE_PATHS } from '@coursera-web/ondemand-common';

import loadOnRoute from 'bundles/common/components/loadOnRoute';
import CourseDataProvider from 'bundles/course-v2/components/CourseDataProvider';
import { ProgrammingAssignmentsSubViews } from 'bundles/item-programming/types';

const ItemPage = loadOnRoute(() => import('bundles/item/components/ItemPage'));

const WiseFlowItem = loadOnRoute(() => import('bundles/lms-interop/wise-flow-item/WiseFlowItem'));
const LtiItem = loadOnRoute(() => import('bundles/item-lti/components/LtiItem'));
const UngradedLabItem = loadOnRoute(() => import('bundles/item-ungraded-lab/components/UngradedLabItem'));
const LearnerCoachItemContainer = loadOnRoute(
  () => import('bundles/coach-item/learner-coach-item/components/LearnerCoachItemContainer')
);
const DiscussionPromptWrapper = loadOnRoute(() => import('bundles/item/components/DiscussionPromptWrapper'));
const GradedDiscussionPromptItem = loadOnRoute(GradedDiscussionPromptItemDynamic);

const PlaceholderItem = loadOnRoute(() => import('bundles/item/components/PlaceholderItem'));

const VideoItemContainer = loadOnRoute(() => import('bundles/item-lecture/components/cds/VideoItemContainer'));
const VideoItemProvider = loadOnRoute(() => import('bundles/item-lecture/components/cds/VideoItemProvider'));

const WidgetItem = loadOnRoute(WidgetItemDynamic);
const ReadingItem = loadOnRoute(() => import('bundles/item-reading/components/ReadingItem'));
const InjectableItemPage = loadOnRoute(InjectableItemPageDynamic);
const ProgrammingItem = loadOnRoute(() => import('bundles/item-programming/components/ProgrammingItem'));
const ProgrammingSubmission = loadOnRoute(() => import('bundles/item-programming/components/ProgrammingSubmission'));
const ProgrammingDiscussions = loadOnRoute(() => import('bundles/item-programming/components/ProgrammingDiscussions'));
const ProgrammingInstructions = loadOnRoute(
  () => import('bundles/item-programming/components/ProgrammingInstructions')
);
const ProgrammingSharing = loadOnRoute(() => import('bundles/item-programming/components/ProgrammingSharing'));
const LaunchedLabLearner = loadOnRoute(() => import('bundles/launched-lab/components/LaunchedLabLearner'));

const PeerItemPage = loadOnRoute(() => import('bundles/peer/components/page/PeerItemPage'));
const PeerItemGiveFeedback = loadOnRoute(() => import('bundles/peer/components/PeerItemGiveFeedback'));
const PeerItemInstructions = loadOnRoute(() => import('bundles/peer/components/PeerItemInstructions'));
const PeerItemReview = loadOnRoute(() => import('bundles/peer/components/PeerItemReview'));
const PeerItemReviewNext = loadOnRoute(() => import('bundles/peer/components/PeerItemReviewNext'));
const PeerItemRubricPreview = loadOnRoute(() => import('bundles/peer/components/rubric-preview/PeerItemRubricPreview'));
const PeerItemSubmit = loadOnRoute(() => import('bundles/peer/components/PeerItemSubmit'));

const PeerItemDiscussionsContainer = loadOnRoute(() => import('bundles/peer/components/PeerItemDiscussionsContainer'));

const TeammateReviewItem = loadOnRoute(() => import('bundles/teammate-review/components/cds/TeammateReviewItem'));
const TeammateReviewCover = loadOnRoute(
  () => import('bundles/teammate-review/components/cover/cds/TeammateReviewCover')
);
const TeammateReview = loadOnRoute(() => import('bundles/teammate-review/components/review/cds/TeammateReview'));
const TeammateReviewGrades = loadOnRoute(() => import('bundles/teammate-review/components/grades/cds/TMRGrades'));

const QuizCoverPage = loadOnRoute(() => import('bundles/quiz-compound-assessment/components/cds/QuizCoverPage'));

const PracticeQuizCoverPage = loadOnRoute(
  () => import('bundles/quiz-compound-assessment/components/cds/PracticeQuizCoverPage')
);
const QuizAttemptPage = loadOnRoute(() => import('bundles/quiz-compound-assessment/components/cds/AttemptPage'));

const PracticeQuizAttemptPage = loadOnRoute(
  () => import('bundles/quiz-compound-assessment/components/cds/PracticeQuizAttemptPage')
);

const SubmittedQuizAttemptPage = loadOnRoute(
  () => import('bundles/quiz-compound-assessment/components/cds/SubmittedAttemptPage')
);

const SubmittedPracticeQuizAttemptPage = loadOnRoute(
  () => import('bundles/quiz-compound-assessment/components/cds/SubmittedPracticeQuizAttemptPage')
);

const AssignmentViewWrapper = loadOnRoute(() => import('bundles/assignment-submission/components/AssignmentViewRoute'));

const AssignmentSubmissionCover = loadOnRoute(
  () => import('bundles/assignment-submission/components/AssignmentCoverRoute')
);

// We now prefix all questions with the path "threads" so add a redirect from old routes.
const generateDiscussionsThreadsRedirect = () => {
  return (
    <Route>
      <Redirect from=":question_id" to="threads/:question_id" />
      <Redirect from=":question_id/replies/:answer_id" to="threads/:question_id/replies/:answer_id" />
      <Redirect
        from=":question_id/replies/:answer_id/comments/:comment_id"
        to="threads/:question_id/replies/:answer_id/comments/:comment_id"
      />
    </Route>
  );
};

export default [
  // routes at top to get component in blank page and avoid ItemPage layout
  <Route key="labRoutes" component={CourseDataProvider}>
    <Route
      path={`${ITEM_ROUTE_PATHS.PROGRAMMING}/:itemId(/:slug)/lab`}
      name="programmingLab"
      getComponent={LaunchedLabLearner}
    />
    <Route
      path={`${ITEM_ROUTE_PATHS.UNGRADED_LAB}/:itemId(/:slug)/lab`}
      name="programmingLab"
      getComponent={LaunchedLabLearner}
    />
    <Route
      path={`${ITEM_ROUTE_PATHS.GRADED_LAB}/:itemId(/:slug)/lab`}
      name="gradedLab"
      getComponent={LaunchedLabLearner}
    />
  </Route>,
  <Route key="itemRoutes" getComponent={VideoItemProvider}>
    <Route component={CourseDataProvider}>
      <Route getComponent={ItemPage}>
        <Route
          path={`${ITEM_ROUTE_PATHS.ACTIVITY}/:injectableActivityType/:item_id(/:slug)`}
          name="activity"
          getComponent={InjectableItemPage}
        />
        <Route path={`${ITEM_ROUTE_PATHS.PEER}/:item_id/`} name="peer" getComponent={PeerItemPage}>
          <Route path=":slug">
            <IndexRoute name="peer.instructions" getComponent={PeerItemInstructions} />
            <Route path="submit" name="peer.submit" getComponent={PeerItemSubmit} />
            <Route path="rubric-preview" name="peer.rubricPreview" getComponent={PeerItemRubricPreview} />
            <Route path="give-feedback" name="peer.give-feedback" getComponent={PeerItemGiveFeedback} />
            <Route path="review-next" name="peer.review-next" getComponent={PeerItemReviewNext} />
            <Route path="review/:submission_id(/admin)" name="peer.review" getComponent={PeerItemReview} />

            <Route path="discussions" name="peer.discussions">
              <IndexRoute getComponent={PeerItemDiscussionsContainer} />
              <Route path="threads/:question_id" getComponent={PeerItemDiscussionsContainer} />
              <Route path="threads/:question_id/replies/:answer_id" getComponent={PeerItemDiscussionsContainer} />
              <Route
                path="threads/:question_id/replies/:answer_id/comments/:comment_id"
                getComponent={PeerItemDiscussionsContainer}
              />

              {generateDiscussionsThreadsRedirect()}
            </Route>
          </Route>
        </Route>
        <Route
          path={`${ITEM_ROUTE_PATHS.DISCUSSION_PROMPT}/:item_id(/:slug)`}
          name="discussionPrompt"
          getComponent={DiscussionPromptWrapper}
        />
        <Route
          path={`${ITEM_ROUTE_PATHS.GRADED_DISCUSSION_PROMPT}/:item_id(/:slug)`}
          name="gradedDiscussionPrompt"
          getComponent={GradedDiscussionPromptItem}
        />
        <Route path={`${ITEM_ROUTE_PATHS.UNGRADED_WIDGET}/:item_id(/:slug)`} name="widget" getComponent={WidgetItem} />
        <Route
          path={`${ITEM_ROUTE_PATHS.GRADED_WIDGET}/:item_id(/:slug)`}
          name="gradedWidget"
          getComponent={WidgetItem}
        />
        <Route path={`${ITEM_ROUTE_PATHS.SUPPLEMENT}/:item_id(/:slug)`} name="reading" getComponent={ReadingItem} />
        <Route path={`${ITEM_ROUTE_PATHS.WISE_FLOW}/:item_id(/:slug)`} name="wiseFlow" getComponent={WiseFlowItem} />
        <Route path={`${ITEM_ROUTE_PATHS.GRADED_LTI}/:item_id(/:slug)`} name="gradedLti" getComponent={LtiItem} />
        <Route path={`${ITEM_ROUTE_PATHS.UNGRADED_LTI}/:item_id(/:slug)`} name="ungradedLti" getComponent={LtiItem} />
        <Route
          path={`${ITEM_ROUTE_PATHS.UNGRADED_LAB}/:item_id(/:slug)`}
          name="ungradedLab"
          getComponent={UngradedLabItem}
        />
        <Route
          path={`${ITEM_ROUTE_PATHS.GRADED_LAB}/:item_id(/:slug)`}
          name="gradedLab"
          getComponent={UngradedLabItem}
        />
        <Route
          path={`${ITEM_ROUTE_PATHS.COACH}/:item_id(/:slug)`}
          name="coach"
          getComponent={LearnerCoachItemContainer}
        />
        <Route
          path={`${ITEM_ROUTE_PATHS.PROGRAMMING}/:item_id(/:slug)`}
          name="programming"
          getComponent={ProgrammingItem}
        >
          <IndexRoute name={ProgrammingAssignmentsSubViews.DEFAULT} getComponent={ProgrammingInstructions} />
          <Route
            path={ProgrammingAssignmentsSubViews.INSTRUCTIONS}
            name={ProgrammingAssignmentsSubViews.INSTRUCTIONS}
            getComponent={ProgrammingInstructions}
          />
          <Route
            path={ProgrammingAssignmentsSubViews.SUBMISSIONS}
            name={ProgrammingAssignmentsSubViews.SUBMISSIONS}
            getComponent={ProgrammingSubmission}
          />
          <Route path="discussions" name={ProgrammingAssignmentsSubViews.DISCUSSIONS}>
            <IndexRoute getComponent={ProgrammingDiscussions} />
            <Route path="threads/:question_id" getComponent={ProgrammingDiscussions} />
            <Route path="threads/:question_id/replies/:answer_id" getComponent={ProgrammingDiscussions} />
            <Route
              path="threads/:question_id/replies/:answer_id/comments/:comment_id"
              getComponent={ProgrammingDiscussions}
            />

            {generateDiscussionsThreadsRedirect()}
          </Route>
          <Route
            path={ProgrammingAssignmentsSubViews.SHARING}
            name={ProgrammingAssignmentsSubViews.SHARING}
            getComponent={ProgrammingSharing}
          />
        </Route>
        <Route />
        {/* On course week page links to items are provided by BE. TMR items have wrong */}
        {/* path: `teammateReview` instead of `teammate-review`. This is a temporary */}
        {/* redirect until we will fix it on BE. */}
        <Redirect from="teammateReview/:item_id(/:slug)" to={`${ITEM_ROUTE_PATHS.TEAMMATE_REVIEW}/:item_id(/:slug)`} />
        <Route
          path={`${ITEM_ROUTE_PATHS.TEAMMATE_REVIEW}/:item_id(/:slug)`}
          name="teammate-review-item"
          getComponent={TeammateReviewItem}
        >
          <IndexRoute name="teammate-review-cover" getComponent={TeammateReviewCover} />
          <Route path="review/:teammateId" name="teammate-review-review" getComponent={TeammateReview} />
          <Route path="grades" name="teammate-review-grades" getComponent={TeammateReviewGrades} />
        </Route>
        <Redirect from="team/:item_id" to={`${ITEM_ROUTE_PATHS.ASSIGNMENT_SUBMISSION}/:item_id`} />
        <Redirect from="team/:item_id/:slug(/:extra)" to={`${ITEM_ROUTE_PATHS.ASSIGNMENT_SUBMISSION}/:item_id/:slug`} />
        {/* Redirect ungraded assignment route from Consumer Home page to assignment-submission because the original URL comes from the BE */}
        <Redirect from="ungradedAssignment/:item_id" to={`${ITEM_ROUTE_PATHS.ASSIGNMENT_SUBMISSION}/:item_id`} />
        <Redirect
          from="ungradedAssignment/:item_id/:slug(/:extra)"
          to={`${ITEM_ROUTE_PATHS.ASSIGNMENT_SUBMISSION}/:item_id/:slug`}
        />
        <Route
          path={`${ITEM_ROUTE_PATHS.ASSIGNMENT_SUBMISSION}/:item_id(/:slug)`}
          name="assignment-submission-cover"
          getComponent={AssignmentSubmissionCover}
        >
          <Route path=":viewType" name="assignment-submission-view" getComponent={AssignmentViewWrapper} />
        </Route>
        <Route path={`${ITEM_ROUTE_PATHS.EXAM}/:item_id(/:slug)`} name="quiz-cover" getComponent={QuizCoverPage}>
          <Route path="attempt" name="quiz-attempt" getComponent={QuizAttemptPage} />
          <Route path="view-attempt" name="quiz-view-attempt" getComponent={SubmittedQuizAttemptPage} />
        </Route>
        <Route
          path={`${ITEM_ROUTE_PATHS.QUIZ}/:item_id(/:slug)`}
          name="practice-quiz-cover"
          getComponent={PracticeQuizCoverPage}
        >
          <Route path="attempt" name="practice-quiz-attempt" getComponent={PracticeQuizAttemptPage} />
          <Route
            path="view-attempt"
            name="practice-quiz-view-attempt"
            getComponent={SubmittedPracticeQuizAttemptPage}
          />
        </Route>
        <Route
          path={`${ITEM_ROUTE_PATHS.PLACEHOLDER}/:item_id(/:slug)`}
          name="placeholder"
          getComponent={PlaceholderItem}
        />
        <Route path={`${ITEM_ROUTE_PATHS.LECTURE}/:item_id(/:slug)`} name="lecture" getComponent={VideoItemContainer} />
      </Route>
    </Route>
  </Route>,
];
